import { Box, Button, Image } from '@wix/design-system';
import { ArrowDownSmall, ArrowUpSmall, ConfirmSmall } from '@wix/wix-ui-icons-common';
import classNames from 'classnames';
import React from 'react';

import { RicosContext } from './../../ricos-context';

import {
  AI_IMAGE_MODAL_ACTION_INSERT,
  AI_IMAGE_MODAL_ACTION_INSERT_ABOVE,
  AI_IMAGE_MODAL_ACTION_INSERT_BELOW,
  AI_IMAGE_MODAL_FEED_ITEM,
} from '../data-hooks';
import { ErrorMessage } from '../error-message/error-message';
import { unreachable } from '../ts-utils';
import styles from './ai-image-modal-scss';
import { Loader } from './loader';
import type { AiImageModalState, Entry } from './slice/types';

type FeedProps = {
  state: AiImageModalState;
  insertImage: (url: Entry, position: 'above' | 'below') => void;
  insertActions: 'single' | 'both';
};

export const Feed: React.FC<FeedProps> = ({ state, insertImage, insertActions }) => {
  const { t } = React.useContext(RicosContext);

  switch (state.type) {
    case 'INITIALIZING':
    case 'INITIAL': {
      return null;
    }
    case 'IN_PROGRESS':
    case 'RESULTS': {
      return (
        <div className={classNames(styles.content, styles.contentScrollReverse)}>
          <Box direction="vertical" paddingTop="SP2" paddingBottom="SP1">
            {state.entries.map((entry, index) => {
              switch (entry.type) {
                case 'GENERATED': {
                  return (
                    <Box
                      direction="vertical"
                      paddingLeft="SP4"
                      paddingRight="SP4"
                      paddingTop="SP2"
                      paddingBottom="SP2"
                      gap="SP2"
                      dataHook={AI_IMAGE_MODAL_FEED_ITEM}
                    >
                      <Box direction="vertical" key={entry.url}>
                        <Image src={entry.url} width={492} height={276} />
                      </Box>
                      <Box gap="tiny">
                        {insertActions === 'both' && (
                          <>
                            <Button
                              dataHook={AI_IMAGE_MODAL_ACTION_INSERT_BELOW}
                              size="small"
                              priority="secondary"
                              prefixIcon={<ArrowDownSmall />}
                              onClick={() => {
                                insertImage(entry, 'below');
                              }}
                            >
                              {t('AIPlugin_Action_InsertBelow')}
                            </Button>
                            <Button
                              dataHook={AI_IMAGE_MODAL_ACTION_INSERT_ABOVE}
                              size="small"
                              priority="secondary"
                              prefixIcon={<ArrowUpSmall />}
                              onClick={() => {
                                insertImage(entry, 'above');
                              }}
                            >
                              {t('AIPlugin_Action_InsertAbove')}
                            </Button>
                          </>
                        )}
                        {insertActions === 'single' && (
                          <Button
                            dataHook={AI_IMAGE_MODAL_ACTION_INSERT}
                            size="small"
                            priority="secondary"
                            prefixIcon={<ConfirmSmall />}
                            onClick={() => {
                              insertImage(entry, 'above');
                            }}
                          >
                            {t('AIPlugin_Action_Insert')}
                          </Button>
                        )}
                      </Box>
                    </Box>
                  );
                }
                case 'GENERATING': {
                  return (
                    <Box dataHook={AI_IMAGE_MODAL_FEED_ITEM}>
                      <Loader
                        width="100%"
                        height={276}
                        duration={10000}
                        maxProgress={93}
                        key={index}
                        paddingTop="SP2"
                        paddingBottom="SP3"
                        paddingLeft="SP4"
                        paddingRight="SP4"
                        showPercentage
                        backgroundColor="grey"
                        loadingText={t('AIPlugin_Loading_GenerateImage')}
                      />
                    </Box>
                  );
                }
                default: {
                  throw unreachable(entry);
                }
              }
            })}
          </Box>
        </div>
      );
    }
    case 'ERROR': {
      return (
        <div className={classNames(styles.content, styles.padded)}>
          <ErrorMessage />
        </div>
      );
    }
    default: {
      throw unreachable(state);
    }
  }
};
